import { useEffect, useMemo, useState } from 'react';
import { Button } from '@coordio/ui';
import { Download as DownloadIcon } from 'lucide-react';
import { fileService } from '../../services/fileService';
import { tryParseMediaProcessingResult } from '../../types/multimodal';
import { MediaProcessingStatus } from './MediaProcessingStatus';

export interface AudioMessageProps {
  fileId: string;
  processedContentJson?: string | null;
}

/**
 * Renders an audio attachment with transcription (if available).
 */
export const AudioMessage = ({ fileId, processedContentJson }: AudioMessageProps) => {
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const result = useMemo(() => tryParseMediaProcessingResult(processedContentJson), [processedContentJson]);

  useEffect(() => {
    let active = true;
    let urlToRevoke: string | null = null;

    (async () => {
      try {
        const resp = await fileService.downloadFileBlob(fileId);
        const blob = resp.data;
        urlToRevoke = URL.createObjectURL(blob);
        if (active) setObjectUrl(urlToRevoke);
      } catch {
        if (active) setObjectUrl(null);
      }
    })();

    return () => {
      active = false;
      if (urlToRevoke) URL.revokeObjectURL(urlToRevoke);
    };
  }, [fileId]);

  const download = async () => {
    const resp = await fileService.downloadFileBlob(fileId);
    const blob = resp.data;
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `attachment-${fileId}`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="mt-2">
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <span className="text-sm font-medium">Audio</span>
        <MediaProcessingStatus processedContentJson={processedContentJson} />
        <Button size="sm" variant="outline" onClick={download}>
          <DownloadIcon className="mr-1 h-3 w-3" />
          Download
        </Button>
      </div>

      {objectUrl && <audio controls src={objectUrl} style={{ width: '100%' }} />}

      {result?.transcription && (
        <p className="text-sm text-muted-foreground mt-2 whitespace-pre-wrap">
          {result.transcription}
        </p>
      )}
    </div>
  );
};
